import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { SelectButton } from 'primereact/selectbutton';
import { Tag } from 'primereact/tag';
import { Menu } from 'primereact/menu';
import { Dialog } from 'primereact/dialog';
import { Tooltip } from 'primereact/tooltip';
import ApiService from '../../services/api';
import { handleApiError } from '../../services/api';
import { useScenarioContext, scenarioActions } from '../../context/ScenarioContext';
import { FavoriteScenario, ScenarioResult, PayerMixItem, ProductFamily, RebateDetail } from '../../types';
import RebateDetailDialog, { RebateDetailTarget } from '../../components/RebateDetailDialog/RebateDetailDialog';
import { useExportScenarios } from '../../hooks/useExportScenarios';
import { usePracticeData, PRACTICE_KEY_DELIMITER, getDisplayNameFromKey } from '../../hooks/usePracticeData';
import './FavoriteScenarios.css';

// Stable color assignment with minimum hue distance between product families
const familyColorCache = new Map<string, string>();
const assignedHues: number[] = [];
const MIN_HUE_DISTANCE = 40;

const getColorForFamily = (familyName: string): string => {
  const cached = familyColorCache.get(familyName);
  if (cached) return cached;

  let hash = 0;
  for (let i = 0; i < familyName.length; i++) {
    hash = familyName.charCodeAt(i) + ((hash << 5) - hash);
  }
  let hue = Math.abs(hash % 360);

  let attempts = 0;
  while (attempts < 360) {
    const tooClose = assignedHues.some(h => {
      const diff = Math.abs(h - hue);
      return Math.min(diff, 360 - diff) < MIN_HUE_DISTANCE;
    });
    if (!tooClose) break;
    hue = (hue + MIN_HUE_DISTANCE) % 360;
    attempts += MIN_HUE_DISTANCE;
  }

  assignedHues.push(hue);
  const color = `hsl(${hue}, 70%, 50%)`;
  familyColorCache.set(familyName, color);
  return color;
};

// Generate signature for a complete scenario (all product families together)
const getScenarioSignature = (scenarioDetails: ScenarioResult[]) => {
  // Sort by product_family_id to ensure consistent ordering
  const sorted = [...scenarioDetails].sort((a, b) => 
    String(a.product_family_id).localeCompare(String(b.product_family_id))
  );
  
  // Create a signature from the distribution of each product family
  const signature = {
    logged_in_user: sorted[0]?.logged_in_user,
    category: sorted[0]?.category,
    distributions: sorted.map(detail => ({
      product_family_id: detail.product_family_id,
      future_distribution: detail.future_distribution,
      invoice_units: detail.invoice_units
    }))
  };
  return JSON.stringify(signature);
};

// Update the ScenarioResults UI cache when unfavoriting
const removeFromScenarioResultsCache = (scenarioDetails: ScenarioResult[]) => {
  try {
    const cached = localStorage.getItem('scenarioiq_favorites_ui_cache');
    
    if (!cached) return;
    
    const cachedFavorites = new Set<string>(JSON.parse(cached) as string[]);
    
    // Generate signature for the complete scenario
    const signature = getScenarioSignature(scenarioDetails);
    cachedFavorites.delete(signature);
    
    // Update cache
    localStorage.setItem('scenarioiq_favorites_ui_cache', JSON.stringify(Array.from(cachedFavorites)));
  } catch (error) {
    console.error('Failed to update ScenarioResults cache');
  }
};

const FavoriteScenarios: React.FC = () => {
  const { state, dispatch } = useScenarioContext();
  const toast = useRef<Toast>(null);
  const exportMenuRef = useRef<Menu>(null);
  const [favorites, setFavorites] = useState<FavoriteScenario[]>([]);
  const [loading, setLoading] = useState(false);
  const latestFavoritesRequestRef = useRef<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<{ [key: string]: boolean }>({});
  const [ncrMode, setNcrMode] = useState<'medicare' | 'weighted'>('medicare');
  // Toggle between per-category-unit (EU) and per-invoice-unit (IU) figures. Always opens on category.
  const [unitMode, setUnitMode] = useState<'category' | 'invoice'>('category');
  // Oral and non-oral favorites are shown in separate views, never interleaved.
  const [inventoryMode, setInventoryMode] = useState<'nonoral' | 'oral'>('nonoral');
  // Oral categories use benchmark (not Medicare) reimbursement terminology.
  const ncrLabel = inventoryMode === 'oral' ? 'Benchmark' : 'Medicare';
  // Per-unit basis derived from the unit toggle.
  const perUnitLabel = unitMode === 'category' ? 'per EU' : 'per IU';
  const perUnitSuffix = unitMode === 'category' ? '_per_eu' : '_per_iu';
  const unitsField = unitMode === 'category' ? 'category_units' : 'invoice_units';
  const unitsHeader = unitMode === 'category' ? 'Category Units' : 'Invoice Units';
  const [payerMixAvailable, setPayerMixAvailable] = useState<boolean>(false);
  // True once the payer-mix fetch has resolved (any outcome). The units toggle is
  // gated on this so it appears together with the async weighted-NCR toggle instead
  // of rendering ahead of it.
  const [payerMixLoaded, setPayerMixLoaded] = useState<boolean>(false);
  const [payerMix, setPayerMix] = useState<PayerMixItem[]>([]);

  // Decision logic (payer viewability) modal state
  const [isDecisionLogicModalVisible, setDecisionLogicModalVisible] = useState(false);
  const [decisionLogicScenario, setDecisionLogicScenario] = useState<{
    scenario_id: number;
    category: string;
    payerAssignments?: Record<string, string>;
    details: ScenarioResult[];
  } | null>(null);
  const [expandedDecisionPayers, setExpandedDecisionPayers] = useState<Record<number, Record<string, boolean>>>({});

  // Rebate drill-down dialog state, same shape as ScenarioResults. The favorite is kept
  // alongside the target because its saved rebate_details and scenario_details rows are
  // what the dialog renders — there is no context state to fall back on here.
  const [rebateDetailContext, setRebateDetailContext] = useState<{
    favorite: FavoriteScenario;
    target: RebateDetailTarget;
  } | null>(null);
  // Separate from the target so closing can animate out with its content still mounted.
  const [rebateDetailVisible, setRebateDetailVisible] = useState(false);
  const rebateDetailRows = useMemo(() => {
    if (!rebateDetailContext) return [];
    const saved = rebateDetailContext.favorite.rebate_details ?? [];
    return saved.filter(d => String(d.product_family_id) === String(rebateDetailContext.target.pfId));
  }, [rebateDetailContext]);
  const rebateDetailSummaryRow = useMemo(() => {
    if (!rebateDetailContext) return undefined;
    return (rebateDetailContext.favorite.scenario_details || []).find(
      r => String(r.product_family_id) === String(rebateDetailContext.target.pfId)
    );
  }, [rebateDetailContext]);

  // Payer eligibility + product families (cached per category)
  const [drugPayerMatrixByCategory, setDrugPayerMatrixByCategory] = useState<Record<string, Record<string, Record<string, boolean>>>>({});
  const [payerPrefsLoading, setPayerPrefsLoading] = useState(false);
  const [payerPrefsError, setPayerPrefsError] = useState<string | null>(null);
  const [productFamiliesByCategory, setProductFamiliesByCategory] = useState<Record<string, ProductFamily[]>>({});
  const [productFamiliesLoading, setProductFamiliesLoading] = useState(false);
  const [productFamiliesError, setProductFamiliesError] = useState<string | null>(null);
  const [updatingFavoriteId, setUpdatingFavoriteId] = useState<number | null>(null);
  const { allCustomers, practiceMap, corporateParentSet, isDemoMode, practiceAccountNumbers, orgDropdownLocked, isInternal } = usePracticeData();
  const [selectedCustomerName, setSelectedCustomerName] = useState<string>('');
  const [userClearedDropdown, setUserClearedDropdown] = useState(false);
  // New: filter state for user/category
  const [selectedFilterUser, setSelectedFilterUser] = useState<string[]>([]);
  const [selectedFilterCategory, setSelectedFilterCategory] = useState<string[]>([]);

  // Export hook
  const { isExporting, handleExport } = useExportScenarios({ filenamePrefix: 'favorite_scenarios', toastRef: toast });
  
    // Compute all unique users and categories from favorites
    const userToCategories = React.useMemo(() => {
      const map: Record<string, Set<string>> = {};
      favorites.forEach(fav => {
        const user = fav.created_user || '';
        const cat = fav.category || '';
        if (!user || !cat) return;
        if (!map[user]) map[user] = new Set();
        map[user].add(cat);
      });
      return map;
    }, [favorites]);

    const categoryToUsers = React.useMemo(() => {
      const map: Record<string, Set<string>> = {};
      favorites.forEach(fav => {
        const user = fav.created_user || '';
        const cat = fav.category || '';
        if (!user || !cat) return;
        if (!map[cat]) map[cat] = new Set();
        map[cat].add(user);
      });
      return map;
    }, [favorites]);

    // Compute eligible options for each dropdown based on the other filter

    // Favorites for the active inventory view; oral vs non-oral are never shown together.
    const modeFavorites = React.useMemo(
      () => favorites.filter(f => Boolean(f.is_oral) === (inventoryMode === 'oral')),
      [favorites, inventoryMode]
    );

    const hasOral = React.useMemo(() => favorites.some(f => Boolean(f.is_oral)), [favorites]);
    const hasNonOral = React.useMemo(() => favorites.some(f => !f.is_oral), [favorites]);

    // If only one inventory type exists (or the current view is empty), snap to the one that has data.
    useEffect(() => {
      if (favorites.length === 0) return;
      if (inventoryMode === 'oral' && !hasOral && hasNonOral) setInventoryMode('nonoral');
      else if (inventoryMode === 'nonoral' && !hasNonOral && hasOral) setInventoryMode('oral');
    }, [favorites.length, hasOral, hasNonOral, inventoryMode]);

    const eligibleUsers = React.useMemo(() => {
      if (selectedFilterCategory.length > 0) {
        // Only users who have saved ALL selected categories
        return Array.from(
          modeFavorites
            .filter(f => selectedFilterCategory.includes(f.category))
            .reduce((acc, f) => { acc.add(f.created_user); return acc; }, new Set())
        );
      }
      return Array.from(new Set(modeFavorites.map(f => f.created_user).filter(Boolean)));
    }, [modeFavorites, selectedFilterCategory]);

    const eligibleCategories = React.useMemo(() => {
      if (selectedFilterUser.length > 0) {
        // Only categories saved by ANY selected user
        return Array.from(
          modeFavorites
            .filter(f => selectedFilterUser.includes(f.created_user))
            .reduce((acc, f) => { acc.add(f.category); return acc; }, new Set())
        );
      }
      return Array.from(new Set(modeFavorites.map(f => f.category).filter(Boolean)));
    }, [modeFavorites, selectedFilterUser]);

    // Filtered favorites for table
    const filteredFavorites = React.useMemo(() => {
      return modeFavorites.filter(fav => {
        const userOk = selectedFilterUser.length === 0 || selectedFilterUser.includes(fav.created_user);
        const catOk = selectedFilterCategory.length === 0 || selectedFilterCategory.includes(fav.category);
        return userOk && catOk;
      });
    }, [modeFavorites, selectedFilterUser, selectedFilterCategory]);

  const computedFavorites = React.useMemo(() => {
    return favorites.map(f => {
      const details = f.scenario_details || [];
      const totalNCR = details.reduce((sum: number, d: ScenarioResult) => sum + Number(d.total_estimated_ncr || 0), 0);
      const totalWeightedNCR = details.reduce((sum: number, d: ScenarioResult) => sum + Number(d.total_weighted_ncr || 0), 0);
      const totalPrice = details.reduce((sum: number, d: ScenarioResult) => sum + Number(d.net_price || 0), 0);
      const totalRebate = details.reduce((sum: number, d: ScenarioResult) => sum + Number(d.total_rebate || 0), 0);
      const totalMedicareReimb = details.reduce((sum: number, d: ScenarioResult) => sum + Number(d.estimated_medicare_reimbursement || 0), 0);
      const totalWeightedReimb = details.reduce((sum: number, d: ScenarioResult) => sum + Number(d.estimated_pvt_reimbursement || 0), 0);
      return {
        ...f,
        _totals: {
          totalNCR,
          totalWeightedNCR,
          totalPrice,
          totalRebate,
          totalMedicareReimb,
          totalWeightedReimb,
        }
      };
    });
  }, [favorites]);

  const loadFavorites = useCallback(async () => {
    if (!state.currentUser) return;
    const requestedUser = state.currentUser;
    latestFavoritesRequestRef.current = requestedUser;
    setLoading(true);
    try {
      const response = await ApiService.getFavoriteScenarios(requestedUser);
      // Discard stale response if user changed while request was in-flight
      if (latestFavoritesRequestRef.current !== requestedUser) return;
      if (response.success && response.favorites) {
        // Filter only active favorites
        const activeFavorites = response.favorites.filter(f => f.is_active);
        // For demo users, only show favorites from allowed practice accounts
        const filtered = isDemoMode
          ? activeFavorites.filter(f => practiceAccountNumbers.includes(f.logged_in_user))
          : activeFavorites;
        setFavorites(filtered);
      }
    } catch (error) {
      // Don't show error toast for stale requests
      if (latestFavoritesRequestRef.current !== requestedUser) return;
      console.error('Failed to load favorites:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load favorites',
        life: 3000,
      });
    } finally {
      if (latestFavoritesRequestRef.current === requestedUser) {
        setLoading(false);
      }
    }
  }, [state.currentUser, isDemoMode, practiceAccountNumbers]);

  const getCustomerNameFromMap = (fav: FavoriteScenario, accountNumber: string): string => {
    if (!fav.account_number_name_map) return 'N/A';
    for (const entry of fav.account_number_name_map) {
      if (entry[accountNumber]) return entry[accountNumber].trim();
    }
    return 'N/A';
  }

  // Once practiceMap is loaded, sync selectedCustomerName from generator page to currentUser
  useEffect(() => {
    if (
      selectedCustomerName &&
      Object.keys(practiceMap).length > 0 &&
      !userClearedDropdown
    ) {
      // If selectedCustomerName is already a unique key in the map, use it directly
      if (practiceMap[selectedCustomerName]) {
        const accountNumber = practiceMap[selectedCustomerName];
        if (accountNumber && accountNumber !== state.currentUser) {
          dispatch(scenarioActions.setCurrentUser(accountNumber));
        }
        return;
      }
      // Otherwise, find by display name match — pick the first key whose display name matches
      const displayName = getDisplayNameFromKey(selectedCustomerName).trim();
      const matchingKey = Object.keys(practiceMap).find(
        (key) => getDisplayNameFromKey(key).trim() === displayName
      );
      if (matchingKey) {
        const accountNumber = practiceMap[matchingKey];
        if (accountNumber && accountNumber !== state.currentUser) {
          setSelectedCustomerName(matchingKey);
          dispatch(scenarioActions.setCurrentUser(accountNumber));
        }
      }
    }
  }, [practiceMap, selectedCustomerName, userClearedDropdown]);

  // Sync dropdown with currentUser from context
  useEffect(() => {
    if (state.currentUser && !userClearedDropdown && Object.keys(practiceMap).length > 0) {
      // If selectedCustomerName is already a valid key in the map for this user, no need to update
      if (selectedCustomerName && practiceMap[selectedCustomerName] === state.currentUser) return;

      // Try to find the unique key in practiceMap that maps to this account number
      const matchingKey = Object.keys(practiceMap).find(
        (key) => practiceMap[key] === state.currentUser
      );
      if (matchingKey) {
        setSelectedCustomerName(matchingKey);
      } else {
        // Fallback: fetch name from API and build the key
        const fetchCustomerName = async () => {
          try {
            const response = await ApiService.getUserName(state.currentUser!);
            if (response.data?.user_name) {
              const keyWithDelim = `${response.data.user_name}${PRACTICE_KEY_DELIMITER}${state.currentUser}`;
              setSelectedCustomerName(keyWithDelim);
            }
          } catch (error) {
            console.error('Failed to fetch customer name:', error);
          }
        };
        fetchCustomerName();
      }
    }
  }, [state.currentUser, practiceMap, userClearedDropdown]);

  // Reactively sync when generator page selection changes (e.g. after selecting a different org)
  useEffect(() => {
    if (userClearedDropdown) return;
    const generatorAccountNumber = state.generatorState.form.logged_in_user;
    if (!generatorAccountNumber) return;
    if (Object.keys(practiceMap).length === 0) return;
    // If dropdown already points to this account number, nothing to do
    if (selectedCustomerName && practiceMap[selectedCustomerName] === generatorAccountNumber) return;
    // Find the matching unique key by account number
    const matchingKey = Object.keys(practiceMap).find(
      (key) => practiceMap[key] === generatorAccountNumber
    );
    if (matchingKey) {
      setSelectedCustomerName(matchingKey);
      if (generatorAccountNumber !== state.currentUser) {
        dispatch(scenarioActions.setCurrentUser(generatorAccountNumber));
      }
    }
  }, [state.generatorState.form.logged_in_user, practiceMap]);

  useEffect(() => {
    if (allCustomers.length === 0 || Object.keys(practiceMap).length === 0) return;
    if (selectedCustomerName || state.currentUser || userClearedDropdown) return;
    const shouldAutoSelect = orgDropdownLocked ||
      (!isInternal && corporateParentSet.has(allCustomers[0]));
    if (!shouldAutoSelect) return;
    const firstKey = allCustomers[0];
    const accountNumber = practiceMap[firstKey];
    if (!firstKey || !accountNumber) return;
    handleCustomerChange(firstKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgDropdownLocked, isInternal, allCustomers, practiceMap, corporateParentSet, selectedCustomerName, state.currentUser, userClearedDropdown]);

  useEffect(() => {
    if (state.currentUser) {
      loadFavorites();
    }
  }, [state.currentUser, loadFavorites]);

  // Refetch payer mix using category endpoint; hide weighted toggle and force medicare if unavailable.
  // Depends on favorites so we always have a valid category to query against.
  useEffect(() => {
    const fetchMix = async () => {
      // Only non-oral categories carry payer mix; oral never shows the weighted toggle.
      const firstCategory = favorites.find(f => f.category && !f.is_oral)?.category;
      if (!state.currentUser || !firstCategory) {
        setPayerMixAvailable(false);
        setNcrMode('medicare');
        setPayerMix([]);
        setPayerMixLoaded(true);
        return;
      }
      try {
        const resp = await ApiService.getPayerMixByCategory({ logged_in_user: state.currentUser, category: firstCategory });
        const mix = ((resp as any)?.mix ?? []) as PayerMixItem[];
        const available = Array.isArray(mix) && mix.length > 0;
        setPayerMixAvailable(available);
        setPayerMix(available ? mix : []);
        if (!available) setNcrMode('medicare');
      } catch {
        setPayerMixAvailable(false);
        setNcrMode('medicare');
        setPayerMix([]);
      } finally {
        setPayerMixLoaded(true);
      }
    };
    // Hide both toggles until this fetch resolves so the units toggle appears
    // together with the weighted-NCR toggle rather than ahead of it.
    setPayerMixLoaded(false);
    fetchMix();
  }, [state.currentUser, favorites]);

  const normalizePayerName = (name: string) => name.trim().toLowerCase().replace(/\s+/g, ' ');

  const payerMixByName = useMemo(() => {
    const map: Record<string, number> = {};
    for (const item of payerMix) {
      if (!item?.payer) continue;
      map[normalizePayerName(item.payer)] = Number(item.percent ?? 0);
    }
    return map;
  }, [payerMix]);

  const formatPfLabel = (name: string) => name ? name.charAt(0).toUpperCase() + name.slice(1) : '—';

  const payerAssignmentsToRows = (
    payerAssignments: Record<string, string> | undefined,
    productFamilyNameById: Record<string, string>,
    payerMixLookupByName: Record<string, number>
  ) => {
    if (!payerAssignments) {
      return [] as Array<{ payer: string; payerPercent?: number; productFamilyId: string; productFamilyName: string }>;
    }

    return Object.entries(payerAssignments)
      .map(([payer, assignedId]) => {
        const productFamilyId = String(assignedId);
        const productFamilyName = productFamilyNameById[productFamilyId] ?? productFamilyId;
        const payerPercent = payerMixLookupByName[normalizePayerName(payer)];
        return { payer, payerPercent, productFamilyId, productFamilyName };
      })
      .sort((a, b) => {
        const ap = typeof a.payerPercent === 'number' && !Number.isNaN(a.payerPercent) ? a.payerPercent : undefined;
        const bp = typeof b.payerPercent === 'number' && !Number.isNaN(b.payerPercent) ? b.payerPercent : undefined;

        if (ap === undefined && bp !== undefined) return 1;
        if (ap !== undefined && bp === undefined) return -1;
        if (ap !== undefined && bp !== undefined && bp !== ap) return bp - ap;

        return a.payer.localeCompare(b.payer);
      });
  };

  const getPayerAssignmentsForFavorite = (fav: FavoriteScenario): Record<string, string> | undefined => {
    const direct = (fav as any)?.payer_assignments ?? (fav as any)?.payerAssignments;
    if (direct && typeof direct === 'object') return direct as Record<string, string>;

    const firstDetail = (fav.scenario_details?.[0] as any) ?? undefined;
    const fromDetail = firstDetail?.payer_assignments ?? firstDetail?.payerAssignments;
    if (fromDetail && typeof fromDetail === 'object') return fromDetail as Record<string, string>;
    return undefined;
  };

  // Flatten all scenario details for export, enriched with payer assignments (payer -> PF name)
  const exportableData = React.useMemo(() => {
    return filteredFavorites.flatMap((fav) => {
      const details = fav.scenario_details || [];
      const payerAssignments = getPayerAssignmentsForFavorite(fav);
      if (!payerAssignments || Object.keys(payerAssignments).length === 0) return details;

      const productFamilyNameById: Record<string, string> = {};
      for (const row of details) {
        const pfId = String((row as any)?.product_family_id ?? '');
        const pfName = String((row as any)?.product_family_name ?? '');
        if (!pfId || !pfName) continue;
        if (!productFamilyNameById[pfId]) productFamilyNameById[pfId] = pfName;
      }

      const payerAssignmentsNamed: Record<string, string> = {};
      for (const [payer, pfIdRaw] of Object.entries(payerAssignments)) {
        const pfId = String(pfIdRaw);
        payerAssignmentsNamed[payer] = productFamilyNameById[pfId] ?? pfId;
      }

      return details.map((row) => ({
        ...(row as any),
        payer_assignments: payerAssignmentsNamed,
      }) as ScenarioResult);
    });
  }, [filteredFavorites]);

  const openDecisionLogicModal = (fav: FavoriteScenario) => {
    const details = fav.scenario_details || [];
    const scenarioId = Number(details?.[0]?.scenario_id);
    const category = String(fav.category || details?.[0]?.category || '');
    const payerAssignments = getPayerAssignmentsForFavorite(fav);

    if (!scenarioId || !category) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Missing data',
        detail: 'Unable to open decision logic for this favorite.',
        life: 3000,
      });
      return;
    }

    setDecisionLogicScenario({ scenario_id: scenarioId, category, payerAssignments, details });
    setDecisionLogicModalVisible(true);
  };

  const closeDecisionLogicModal = () => {
    setDecisionLogicModalVisible(false);
    setDecisionLogicScenario(null);
  };

  // Fetch payer preferences + product families for the modal category
  useEffect(() => {
    const userId = state.currentUser;
    const category = decisionLogicScenario?.category;
    if (!userId || !category || !isDecisionLogicModalVisible) return;

    let cancelled = false;

    const run = async () => {
      // Payer preferences (drug payer matrix)
      if (!drugPayerMatrixByCategory[category]) {
        setPayerPrefsLoading(true);
        setPayerPrefsError(null);
        try {
          const resp = await ApiService.getDrugPayerMatrix(userId, category);
          const matrix = (resp as any)?.drugPayerMatrix || (resp as any)?.preferences || {};
          if (!cancelled) {
            setDrugPayerMatrixByCategory((prev) => ({ ...prev, [category]: matrix }));
          }
        } catch (e) {
          if (!cancelled) {
            setDrugPayerMatrixByCategory((prev) => ({ ...prev, [category]: {} }));
            setPayerPrefsError(handleApiError(e));
          }
        } finally {
          if (!cancelled) setPayerPrefsLoading(false);
        }
      }

      // Product families (names)
      if (!productFamiliesByCategory[category]?.length) {
        setProductFamiliesLoading(true);
        setProductFamiliesError(null);
        try {
          const resp = await ApiService.getCategoryProductFamiliesNames(userId, category);
          const families = (resp as any)?.data?.product_families ?? (resp as any)?.product_families ?? [];
          if (!cancelled) {
            setProductFamiliesByCategory((prev) => ({ ...prev, [category]: families as ProductFamily[] }));
          }
        } catch (e) {
          if (!cancelled) {
            setProductFamiliesError(handleApiError(e));
          }
        } finally {
          if (!cancelled) setProductFamiliesLoading(false);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.currentUser, decisionLogicScenario?.category, isDecisionLogicModalVisible]);

  const drugPayerMatrixByNormalizedPayer = useMemo(() => {
    const category = decisionLogicScenario?.category;
    const matrix = category ? (drugPayerMatrixByCategory[category] ?? {}) : {};

    const out: Record<string, Record<string, boolean>> = {};
    Object.entries(matrix || {}).forEach(([productFamilyId, row]) => {
      const normalizedRow: Record<string, boolean> = {};
      Object.entries(row || {}).forEach(([payer, allowed]) => {
        normalizedRow[normalizePayerName(String(payer))] = (allowed as any) === true;
      });
      out[String(productFamilyId)] = normalizedRow;
    });
    return out;
  }, [drugPayerMatrixByCategory, decisionLogicScenario?.category]);

  // Ensure UI refresh respects NCR mode changes without collapsing expanded rows.
  // Table remount keys handle re-rendering; keep expansion state for easier comparison.

  const handleCustomerChange = async (selectedKey: string) => {
    if (!selectedKey || selectedKey === 'Select a customer') {
      setSelectedCustomerName('');
      setFavorites([]);
      setUserClearedDropdown(true);
      return;
    }
    setSelectedCustomerName(selectedKey);
    setUserClearedDropdown(false);
    try {
      // Get account number from our practice map using the unique key
      const accountNumber = practiceMap[selectedKey];
      if (accountNumber) {
        dispatch(scenarioActions.setCurrentUser(accountNumber));
        dispatch(scenarioActions.setGeneratorState({
        form: { ...state.generatorState.form, logged_in_user: accountNumber },
        }));
        // loadFavorites will be triggered by the useEffect watching state.currentUser
      }
    } catch (error) {
      console.error('Failed to fetch user ID:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load customer data',
        life: 3000,
      });
    }
  };

  const toggleFavorite = async (fav: FavoriteScenario) => {
    // Demo accounts are read-only server-side (423). Stop before the optimistic
    // star flip so the row never shows a state the database will not accept.
    if (isDemoMode) {
      toast.current?.show({
        severity: 'info',
        summary: 'Read-only demo',
        detail: 'Favorites cannot be changed from a demo account.',
        life: 3000,
      });
      return;
    }

    const newIsActive = !fav.is_active;
    
    // Set updating state for this specific favorite
    setUpdatingFavoriteId(fav.id);
    
    // Immediately update the star visually
    setFavorites(favorites.map(f => 
      f.id === fav.id ? { ...f, is_active: newIsActive } : f
    ));

    try {
      // Remove updated_by if present to prevent API issues
      const resultsToUpdate = fav.scenario_details.map(d => {
        const { updated_by, ...rest } = d as any;
        return { ...rest, is_active: newIsActive };
      });

      await ApiService.updateFavoriteScenario({
        user_id: fav.logged_in_user,
        category: fav.category,
        scenario_results: resultsToUpdate
      });

      toast.current?.show({
        severity: 'info',
        summary: newIsActive ? 'Favorite Restored' : 'Favorite Removed',
        detail: newIsActive ? 'Scenario restored to favorites' : 'Scenario removed from favorites',
        life: 2000,
      });
      
      // If unfavoriting, remove from ScenarioResults cache
      if (!newIsActive) {
        removeFromScenarioResultsCache(fav.scenario_details);
      }
      
      // Reload favorites to get source of truth from database
      await loadFavorites();
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to update favorite status',
        life: 3000,
      });
      // Reload on error too to ensure correct state
      await loadFavorites();
    } finally {
      // Clear updating state
      setUpdatingFavoriteId(null);
    }
  };

  const formatCurrency = (value: number | null | undefined): string => {
    if (value === null || value === undefined) value = 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '0';
    return Number(value).toLocaleString();
  };

  const formatPercentage = (value: number): string => {
    return Math.round(value * 100) + '%';
  };

  const distributionBodyTemplate = (rowData: ScenarioResult) => {
    return (
      <div className="flex align-items-center gap-2">
        <div
          className="distribution-bar"
          style={{
            width: '60px',
            height: '8px',
            backgroundColor: '#e5e7eb',
            borderRadius: '4px',
            overflow: 'hidden'
          }}
        >
          <div
            style={{
              width: `${rowData.future_distribution * 100}%`,
              height: '100%',
              backgroundColor: getColorForFamily(rowData.product_family_name),
              borderRadius: '4px'
            }}
          />
        </div>
        <span className="text-sm font-medium">
          {formatPercentage(rowData.future_distribution)}
        </span>
      </div>
    );
  };

  const currencyBodyTemplate = (field: string) => (rowData: ScenarioResult) => {
    const value = (rowData as any)[field];
    return (
      <span className={value && value > 0 ? 'text-green-600 font-semibold' : 'text-gray-500'}>
        {formatCurrency(value)}
      </span>
    );
  };

  const totalPriceTemplate = (rowData: ScenarioResult) => {
    const netPrice = rowData.net_price ?? 0;
    return (
      <span className={(netPrice) > 0 ? 'text-green-600 font-semibold' : 'text-gray-500'}>
        {formatCurrency(netPrice)}
      </span>
    );
  };

  const numberBodyTemplate = (field: string) => (rowData: ScenarioResult) => {
    const value = (rowData as any)[field];
    return formatNumber(value);
  };

  const renderRowExpansion = (rowData: FavoriteScenario) => {
    const details: ScenarioResult[] = rowData.scenario_details || [];
    const payerAssignments = getPayerAssignmentsForFavorite(rowData);
    const hasPayerAssignments = payerAssignments && Object.keys(payerAssignments).length > 0;

    // Same gating as ScenarioResults, but against the rows saved with this favorite:
    // presence decides whether the drill-down link renders, the flag picks the tooltip
    // wording. Families with no contract are saved as placeholder rows, so only a real
    // contract row or a manual adjustment counts.
    const savedRebateDetails: RebateDetail[] = rowData.rebate_details ?? [];
    const pfRebateEarning = new Map<string, boolean>();
    savedRebateDetails.forEach(d => {
      if (!d.has_contract && d.rebate_source !== 'Excluded') return;
      const key = String(d.product_family_id);
      pfRebateEarning.set(key, (pfRebateEarning.get(key) ?? false) || Number(d.rebate_earned || 0) > 0);
    });

    const openRebateDetail = (detail: ScenarioResult, label: string) => {
      setRebateDetailContext({
        favorite: rowData,
        target: {
          scenarioId: Number(detail.scenario_id),
          pfId: String(detail.product_family_id),
          pfLabel: label,
        },
      });
      setRebateDetailVisible(true);
    };

    return (
      <div className="expansion-fit-viewport" style={{ padding: '0 1rem 1rem 1rem' }}>
        <div className="flex align-items-center gap-3 mb-3 flex-wrap">
          <h5 className="m-0">Detailed Breakdown</h5>
          <div className="flex align-items-center gap-3 flex-wrap flex-1 min-w-0">
            {details.map((detail) => (
              <div key={detail.product_family_id} className="flex align-items-center gap-2">
                <div
                  style={{
                    width: '20px',
                    height: '12px',
                    backgroundColor: getColorForFamily(detail.product_family_name),
                    borderRadius: '3px',
                  }}
                />
                <span className="text-sm text-600">
                  {detail.product_family_name.charAt(0).toUpperCase() + detail.product_family_name.slice(1)}
                </span>
              </div>
            ))}
          </div>
          <div className="ml-auto flex align-items-center gap-2 flex-shrink-0">
            <Button
              label="View payer assignments"
              icon="pi pi-sitemap"
              className="p-button-sm p-button-outlined white-space-nowrap"
              onClick={() => openDecisionLogicModal(rowData)}
              disabled={!hasPayerAssignments}
            />
          </div>
        </div>
        <DataTable key={`favorite-details-${ncrMode}-${unitMode}`} className="favorites-headers-nowrap" value={details} responsiveLayout="scroll" size="small">
          <Column
            field="product_family_name"
            header="Product Family"
            body={(detail: ScenarioResult) => {
              const label = detail.product_family_name
                ? detail.product_family_name.charAt(0).toUpperCase() + detail.product_family_name.slice(1)
                : '';
              // Only linked when saved rows exist, so the dialog cannot open onto nothing.
              // "No contracts" vs "nothing saved" are different claims — favorites saved
              // before rebate details were persisted get the second wording.
              const isEarning = pfRebateEarning.get(String(detail.product_family_id));
              // A direct rebate is a column on this row rather than a rebate row, so it can
              // be the only thing the drill-down has to show -- the link opens for it too.
              const hasDirectRebate = Number(detail.total_direct_rebate ?? 0) > 0;
              const hasRebates = isEarning !== undefined || hasDirectRebate;
              const rebateTip = hasRebates
                ? (isEarning || hasDirectRebate
                    ? 'View projected rebate performance'
                    : 'View potential rebate performance')
                : savedRebateDetails.length > 0
                  ? 'No rebate contracts exist for this product family'
                  : 'Rebate detail was not saved with this favorite';
              // PrimeReact Tooltip, not a native `title`, so it picks up .p-tooltip styling.
              const rebateTipId = `fav-rebate-tip-${rowData.id}-${detail.product_family_id}`;
              return (
                <span className="flex align-items-center gap-1">
                  {hasRebates ? (
                    <span
                      id={rebateTipId}
                      role="button"
                      tabIndex={0}
                      data-pr-tooltip={rebateTip}
                      data-pr-position="top"
                      onClick={() => openRebateDetail(detail, label)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openRebateDetail(detail, label); } }}
                      style={{ color: 'var(--primary-color)', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: '2px' }}
                    >
                      {label}
                    </span>
                  ) : (
                    <span
                      id={rebateTipId}
                      style={{ cursor: 'default' }}
                      data-pr-tooltip={rebateTip}
                      data-pr-position="top"
                    >
                      {label}
                    </span>
                  )}
                  <Tooltip target={`#${rebateTipId}`} />
                </span>
              );
            }}
          />
          <Column field="future_distribution" header="Distribution" body={distributionBodyTemplate} />
          {/* <Column field="invoice_units" header="Invoice Units" body={numberBodyTemplate('invoice_units')} /> */}
          <Column field={unitsField} header={unitsHeader} body={numberBodyTemplate(unitsField)} />
          {ncrMode === 'medicare' ? (
            <Column field="estimated_medicare_reimbursement" header={`${ncrLabel} Reimb.`} body={currencyBodyTemplate('estimated_medicare_reimbursement')} />
          ) : (
            <Column field="estimated_pvt_reimbursement" header="Weighted Reimb." body={currencyBodyTemplate('estimated_pvt_reimbursement')} />
          )}
          {ncrMode === 'medicare' ? (
            <Column field={`estimated_medicare_reimbursement${perUnitSuffix}`} header={`${ncrLabel} Reimb. ${perUnitLabel}`} body={currencyBodyTemplate(`estimated_medicare_reimbursement${perUnitSuffix}`)} />
          ) : (
            <Column field={`estimated_pvt_reimbursement${perUnitSuffix}`} header={`Weighted Reimb. ${perUnitLabel}`} body={currencyBodyTemplate(`estimated_pvt_reimbursement${perUnitSuffix}`)} />
          )}
          <Column field="total_admin_fee" header="Admin Fee" body={currencyBodyTemplate('total_admin_fee')} />
          <Column field="price" header="Price" body={totalPriceTemplate} />
          <Column field="total_rebate" header="Rebate" body={currencyBodyTemplate('total_rebate')} />
          {ncrMode === 'medicare' ? (
            <Column field="total_estimated_ncr" header={`${ncrLabel} NCR`} body={currencyBodyTemplate('total_estimated_ncr')} />
          ) : (
            <Column field="total_weighted_ncr" header="Weighted NCR" body={currencyBodyTemplate('total_weighted_ncr')} />
          )}
          {ncrMode === 'medicare' ? (
            <Column field={`total_estimated_ncr${perUnitSuffix}`} header={`${ncrLabel} NCR ${perUnitLabel}`} body={currencyBodyTemplate(`total_estimated_ncr${perUnitSuffix}`)} />
          ) : (
            <Column field={`total_weighted_ncr${perUnitSuffix}`} header={`Weighted NCR ${perUnitLabel}`} body={currencyBodyTemplate(`total_weighted_ncr${perUnitSuffix}`)} />
          )}
        </DataTable>
      </div>
    );
  };

  const renderDecisionLogicContent = (scenario: NonNullable<typeof decisionLogicScenario>) => {
    const details = scenario.details || [];
    const categoryKey = scenario.category || details?.[0]?.category;
    const allFamiliesForCategory = categoryKey ? (productFamiliesByCategory[categoryKey] ?? []) : [];

    const productFamilyNameById = allFamiliesForCategory
      .reduce((acc, pf) => {
        const id = pf.product_family_id;
        const name = pf.product_family_name;
        if (id !== null && id !== undefined && name) {
          acc[String(id)] = String(name);
        }
        return acc;
      }, {} as Record<string, string>);

    details.forEach((row) => {
      const id = row.product_family_id;
      const name = row.product_family_name;
      if (id !== null && id !== undefined && name) {
        productFamilyNameById[String(id)] = String(name);
      }
    });

    const payerRows = payerAssignmentsToRows(scenario.payerAssignments, productFamilyNameById, payerMixByName);
    const totalAllocatedPercent = payerRows.reduce((sum, r) => {
      const pct = typeof r.payerPercent === 'number' && !Number.isNaN(r.payerPercent) ? r.payerPercent : 0;
      return sum + pct;
    }, 0);
    const hasAnyAllocatedPercent = payerRows.some((r) => typeof r.payerPercent === 'number' && !Number.isNaN(r.payerPercent));

    const payerBreakdownProductFamilies: Array<{ productFamilyId: string; productFamilyName: string }> =
      allFamiliesForCategory.length > 0
        ? allFamiliesForCategory
            .filter((pf) => pf.product_family_id !== null && pf.product_family_id !== undefined)
            .map((pf) => ({
              productFamilyId: String(pf.product_family_id),
              productFamilyName: String(pf.product_family_name ?? ''),
            }))
        : details
            .reduce((acc, d) => {
              const id = String(d.product_family_id);
              if (!acc.some((x) => x.productFamilyId === id)) {
                acc.push({ productFamilyId: id, productFamilyName: String(d.product_family_name || '') });
              }
              return acc;
            }, [] as Array<{ productFamilyId: string; productFamilyName: string }>);

    const expandedForScenario = expandedDecisionPayers[scenario.scenario_id] ?? {};

    const toggleExpandedPayer = (payer: string) => {
      setExpandedDecisionPayers((prev) => {
        const current = prev[scenario.scenario_id] ?? {};
        const isOpen = current[payer] === true;
        const nextForScenario = isOpen ? {} : { [payer]: true };
        return { ...prev, [scenario.scenario_id]: nextForScenario };
      });
    };

    const payerRowExpansionTemplate = (payerRow: any) => {
      if (payerPrefsLoading || productFamiliesLoading) return <div className="text-500 text-sm">Loading…</div>;
      if (payerPrefsError) return <div className="text-500 text-sm">{payerPrefsError}</div>;
      if (productFamiliesError) return <div className="text-500 text-sm">{productFamiliesError}</div>;

      const payer = String(payerRow.payer);

      if (payerBreakdownProductFamilies.length === 0) {
        return <div className="text-500 text-sm">No product families found for this scenario.</div>;
      }

      return (
        <div style={{ padding: '0.5rem 0.75rem' }}>
          <DataTable value={payerBreakdownProductFamilies} responsiveLayout="scroll" size="small" className="p-datatable-sm">
            <Column
              header="Product Family"
              body={(r) => {
                const rawName = String((r as any).productFamilyName || '');
                const label = rawName ? formatPfLabel(rawName) : '—';
                const color = rawName ? getColorForFamily(rawName) : '#d1d5db';
                return (
                  <div className="flex align-items-center gap-2">
                    <span style={{ width: 12, height: 12, backgroundColor: color, borderRadius: 3, border: '1px solid rgba(0,0,0,0.15)' }} />
                    <span>{label}</span>
                  </div>
                );
              }}
            />
            <Column
              header="Allowed"
              body={(r) => {
                const pfId = String((r as any).productFamilyId);
                const allowed = (drugPayerMatrixByNormalizedPayer?.[pfId]?.[normalizePayerName(payer)] ?? true) === true;
                return <Tag value={allowed ? 'Allowed' : 'Not allowed'} severity={allowed ? 'success' : 'danger'} />;
              }}
              style={{ width: '10rem' }}
            />
          </DataTable>
        </div>
      );
    };

    return (
      <div className="mb-3">
        <div className="mb-2"><strong>Payer assignments:</strong></div>
        {payerRows.length === 0 ? (
          <span className="text-500">—</span>
        ) : (
          <DataTable
            value={payerRows}
            responsiveLayout="scroll"
            size="small"
            className="p-datatable-sm"
            dataKey="payer"
            expandedRows={expandedForScenario}
            rowExpansionTemplate={payerRowExpansionTemplate}
            onRowClick={(e) => {
              const payer = (e.data as any)?.payer;
              if (!payer) return;
              toggleExpandedPayer(String(payer));
            }}
          >
            <Column
              field="payer"
              header="Payer (% of total)"
              style={{ minWidth: '260px' }}
              footer={() => {
                const percentLabel = hasAnyAllocatedPercent
                  ? new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(totalAllocatedPercent) + '%'
                  : '—';
                return (
                  <span>
                    Total % allocated <span className="text-500">({percentLabel})</span>
                  </span>
                );
              }}
              body={(r) => {
                const isExpanded = expandedForScenario[String((r as any).payer)] === true;
                const raw = typeof (r as any).payerPercent === 'number' && !Number.isNaN((r as any).payerPercent)
                  ? (r as any).payerPercent
                  : undefined;
                const percentLabel = raw === undefined ? '' : ` (${raw}%)`;
                return (
                  <span>
                    <i className={`pi ${isExpanded ? 'pi-chevron-down' : 'pi-chevron-right'}`} style={{ marginRight: 8, color: '#6b7280' }} />
                    {(r as any).payer}
                    {percentLabel && <span className="text-500">{percentLabel}</span>}
                  </span>
                );
              }}
            />
            <Column
              header="Product Family"
              footer={() => <span />}
              body={(r) => (
                (() => {
                  const raw = ((r as any).productFamilyName && (r as any).productFamilyName !== (r as any).productFamilyId)
                    ? String((r as any).productFamilyName)
                    : '';
                  if (!raw) return <span>—</span>;

                  const label = formatPfLabel(raw);
                  const color = getColorForFamily(raw);

                  return (
                    <div className="flex align-items-center gap-2">
                      <span style={{ width: 12, height: 12, backgroundColor: color, borderRadius: 3, border: '1px solid rgba(0,0,0,0.15)' }} />
                      <span>{label}</span>
                    </div>
                  );
                })()
              )}
            />
          </DataTable>
        )}
      </div>
    );
  };

  return (
    <div className="favorite-scenarios-container">
      <Toast ref={toast} />

      <Card>
          <div className="flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
            <div>
              <h2 className="m-0 text-2xl font-bold text-900">Favorite Scenarios</h2>
              <div className="text-500 text-sm mt-1">
                {favorites.length} saved scenario{favorites.length === 1 ? '' : 's'}
                {selectedCustomerName && getDisplayNameFromKey(selectedCustomerName).trim()
                  ? ` \u00b7 ${getDisplayNameFromKey(selectedCustomerName)}`
                  : ''}
              </div>
            </div>
            <div className="flex align-items-center gap-2">
              {payerMixLoaded && (payerMixAvailable || inventoryMode === 'oral') && (
                <SelectButton
                  value={ncrMode}
                  onChange={(e) => { if (e.value) setNcrMode(e.value); }}
                  optionDisabled={(option) => inventoryMode === 'oral' && option.value === 'weighted'}
                  options={[
                    { label: `${ncrLabel} NCR`, value: 'medicare' },
                    { label: 'Weighted NCR', value: 'weighted' },
                  ]}
                />
              )}
              {payerMixLoaded && favorites.length > 0 && (
                <SelectButton
                  value={unitMode}
                  onChange={(e) => { if (e.value) setUnitMode(e.value); }}
                  options={[
                    { label: 'Category Units', value: 'category' },
                    { label: 'Invoice Units', value: 'invoice' },
                  ]}
                />
              )}
              <Menu
                model={[
                  { label: 'Export as CSV', icon: 'pi pi-download', command: () => handleExport('csv', exportableData, null, inventoryMode === 'oral') },
                  { label: 'Export as Excel', icon: 'pi pi-file-excel', command: () => handleExport('excel', exportableData, null, inventoryMode === 'oral') },
                ]}
                popup
                ref={exportMenuRef}
              />
              <Button
                label="Export"
                icon="pi pi-download"
                className="p-button-outlined p-button-sm"
                onClick={(e) => exportMenuRef.current?.toggle(e)}
                disabled={isExporting || exportableData.length === 0}
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-3 mb-3 align-items-end">
            <div className="flex flex-wrap gap-3 align-items-end">
              {hasOral && hasNonOral && (
                <div className="flex flex-column gap-1">
                  <label className="font-semibold text-sm mb-1">Inventory Type</label>
                  <SelectButton
                    value={inventoryMode}
                    onChange={(e) => {
                      if (!e.value) return;
                      setInventoryMode(e.value);
                      setSelectedFilterUser([]);
                      setSelectedFilterCategory([]);
                      if (e.value === 'oral') setNcrMode('medicare');
                    }}
                    options={[
                      { label: 'Non-Oral', value: 'nonoral' },
                      { label: 'Oral', value: 'oral' },
                    ]}
                  />
                </div>
              )}
              <div className="flex flex-column gap-1" style={{ minWidth: 220 }}>
                <div className="sup-label-field">
                  <label htmlFor="customer-dropdown" className="font-semibold">Organization Name</label>
                  <Dropdown
                    id="customer-dropdown"
                    value={selectedCustomerName}
                    options={allCustomers.map(c => ({ label: getDisplayNameFromKey(c), value: c }))}
                    onChange={(e) => handleCustomerChange(e.value)}
                    placeholder="Select an organization"
                    filter
                    filterBy="label"
                    className="w-20rem"
                    disabled={allCustomers.length === 0 || orgDropdownLocked}
                    scrollHeight="400px"
                    appendTo="self"
                    valueTemplate={(option) => {
                      if (!option) return <span>Select an organization</span>;
                      const displayName = typeof option === 'string'
                        ? getDisplayNameFromKey(option)
                        : getDisplayNameFromKey(option.value || '');
                      {  return <span>{displayName.trim() || 'Select an organization'}</span>; }
                    }}
                    itemTemplate={(option) => {
                      const uniqueKey = option.value;
                      const displayName = getDisplayNameFromKey(uniqueKey);
                      const isCorporate = corporateParentSet.has(uniqueKey);
                      const isChildPractice = displayName.startsWith('    '); // Child practices are indented
                      const isStandalonePractice = !isCorporate && !isChildPractice; // Practice without a parent
                      return (
                        <div className="flex align-items-center gap-2">
                          {isCorporate ? (
                            <i className="pi pi-building" style={{ fontSize: '1.1rem', color: '#173A63' }} />
                          ) : isChildPractice ? (
                            <i className="pi pi-home" style={{ fontSize: '0.9rem', color: '#6c757d', marginLeft: '1rem' }} />
                          ) : isStandalonePractice ? (
                            <i className="pi pi-briefcase" style={{ fontSize: '0.9rem', color: '#495057' }} />
                          ) : (
                            <i className="pi pi-home" style={{ fontSize: '0.9rem', color: '#6c757d' }} />
                          )}
                          <span>{displayName.trim()}</span>
                        </div>
                      );
                    }}
                  />
                </div>
              </div>

              {selectedCustomerName && getDisplayNameFromKey(selectedCustomerName).trim() && (
                <>
                  <div className="flex flex-column gap-1" style={{ minWidth: 180 }}>
                    <div className="sup-label-field">
                      <label htmlFor="user-filter" className="font-semibold">Created User</label>
                      <Dropdown
                        id="user-filter"
                        value={selectedFilterUser}
                        options={eligibleUsers.map(u => ({ label: u, value: u }))}
                        onChange={e => setSelectedFilterUser(e.value || [])}
                        placeholder="All Users"
                        showClear
                        multiple
                        className="w-16rem"
                        disabled={eligibleUsers.length === 0 || isDemoMode}
                      />
                    </div>
                  </div>
                  <div className="flex flex-column gap-1">
                    <div className="sup-label-field">
                      <label htmlFor="category-filter" className="font-semibold">Competitive Therapeutic Area</label>
                      <Dropdown
                        id="category-filter"
                        value={selectedFilterCategory}
                        options={eligibleCategories.map(c => ({ label: c, value: c }))}
                        onChange={e => setSelectedFilterCategory(e.value || [])}
                        placeholder="All CTAs"
                        showClear
                        multiple
                        className="w-16rem"
                        disabled={eligibleCategories.length === 0}
                      />
                    </div>
                  </div>

                  <Button
                    label="Clear all"
                    icon="pi pi-times"
                    className="p-button-sm p-button-text"
                    onClick={() => {
                      setSelectedFilterUser([]);
                      setSelectedFilterCategory([]);
                    }}
                  />
                </>
              )}
            </div>
          </div>

        <DataTable
          // isDemoMode is in the key because it arrives asynchronously from
          // usePracticeData: DataTable memoizes its body templates, so without a key
          // change a late flag never reaches the star cell (no dimming, no tooltip).
          key={`favorites-${ncrMode}-demo-${isDemoMode}`}
          value={filteredFavorites.map(f => computedFavorites.find(cf => cf.id === f.id) || f)}
          loading={loading}
          expandedRows={expandedRows}
          onRowToggle={(e) => setExpandedRows(e.data as any)}
          rowExpansionTemplate={(rowData) => renderRowExpansion(rowData)}
          dataKey="id"
          paginator
          rows={10}
          emptyMessage="No favorite scenarios found."
          className="p-datatable-sm favorites-headers-nowrap expansion-fit-table"
        >
          <Column
            header={() => null}
            body={(rowData) => {
              const isUpdating = updatingFavoriteId === rowData.id;
              if (isUpdating) {
                return (
                  <i 
                    className="pi pi-spin pi-spinner" 
                    style={{ fontSize: '1.2rem', color: '#6c757d' }}
                  />
                );
              }
              // Per-row class so each Tooltip binds to its own star rather than to every
              // star in the table. What makes a late-arriving isDemoMode reach this cell at
              // all is the isDemoMode segment in the DataTable key above: DataTable memoizes
              // body templates, so without it the flag never lands here.
              const demoTipClass = `favorite-star-demo-${rowData.id}`;
              return (
                <>
                  {isDemoMode && <Tooltip target={`.${demoTipClass}`} position="top" />}
                  <i
                    className={`pi ${rowData.is_active ? 'pi-star-fill text-yellow-500' : 'pi-star text-gray-400'} ${isDemoMode ? `opacity-50 ${demoTipClass}` : 'cursor-pointer'}`}
                    style={{ fontSize: '1.2rem' }}
                    data-pr-tooltip={isDemoMode ? 'Read-only demo account' : undefined}
                    onClick={() => {
                      if (isDemoMode) return;
                      toggleFavorite(rowData);
                    }}
                  />
                </>
              );
            }}
            style={{ width: '3rem', textAlign: 'center' }}
          />
          <Column expander style={{ width: '3em' }} sortable={false} />
          <Column 
            field="category" 
            header={<span title="Competitive Therapeutic Area">CTA</span>}
            sortable
            body={(rowData) => (
              <div className="flex align-items-center">
                {rowData.category}
                {rowData.is_expired && <Tag className="ml-2" value="Expired" severity="warning" />}
              </div>
            )}
          />
          {ncrMode === 'medicare' ? (
            <Column 
              key="total-medicare-reimb-col"
              header={`${ncrLabel} Reimb.`} 
              body={(rowData) => formatCurrency((rowData as any)._totals?.totalMedicareReimb ?? 0)} 
              sortable
              sortField="_totals.totalMedicareReimb"
            />
          ) : (
            <Column 
              key="total-weighted-reimb-col"
              header="Weighted Reimb." 
              body={(rowData) => formatCurrency((rowData as any)._totals?.totalWeightedReimb ?? 0)} 
              sortable
              sortField="_totals.totalWeightedReimb"
            />
          )}
          <Column 
            header="Price" 
            body={(rowData) => formatCurrency((rowData as any)._totals?.totalPrice ?? 0)} 
            sortable
            sortField="_totals.totalPrice"
          />
          <Column 
            header="Rebate" 
            body={(rowData) => formatCurrency((rowData as any)._totals?.totalRebate ?? 0)} 
            sortable
            sortField="_totals.totalRebate"
          />
          {ncrMode === 'medicare' ? (
            <Column 
              key="total-ncr-col"
              header={`${ncrLabel} NCR`} 
              body={(rowData) => formatCurrency((rowData as any)._totals?.totalNCR ?? 0)} 
              sortable
              sortField="_totals.totalNCR"
            />
          ) : (
            <Column 
              key="total-weighted-ncr-col"
              header="Weighted NCR" 
              body={(rowData) => formatCurrency((rowData as any)._totals?.totalWeightedNCR ?? 0)} 
              sortable
              sortField="_totals.totalWeightedNCR"
            />
          )}
          <Column field="created_user" header="Created User" sortable body={isDemoMode ? () => 'Demo User' : undefined} />
          <Column field="created_date" header="Date Saved" body={(r) => new Date(r.created_date).toLocaleDateString()} sortable />
          {/* <Column field="logged_in_user" header="Practice" sortable body={(rowData) => { original practice name logic }} /> */}
          <Column 
            field="logged_in_user" 
            header="Practice" 
            sortable
            body={(rowData: FavoriteScenario) => {
              // Resolve display name from practiceMap using the favorite's account number
              const matchingKey = Object.keys(practiceMap).find(
                (key) => practiceMap[key] === rowData.logged_in_user
              );
              if (matchingKey) {
                return getDisplayNameFromKey(matchingKey).trim();
              }
              // Fallback to the favorite's own account_number_name_map
              return getCustomerNameFromMap(rowData, rowData.logged_in_user);
            }} 
          />
        </DataTable>
      </Card>

      <Dialog
        header="Decision logic"
        visible={isDecisionLogicModalVisible}
        onHide={closeDecisionLogicModal}
        style={{ width: '100vw', maxWidth: '1100px' }}
        breakpoints={{ '960px': '100vw' }}
        modal
        dismissableMask={true}
        blockScroll
        contentStyle={{ maxHeight: '80vh', overflow: 'auto' }}
      >
        {decisionLogicScenario ? renderDecisionLogicContent(decisionLogicScenario) : null}
      </Dialog>

      <RebateDetailDialog
        visible={rebateDetailVisible}
        onHide={() => setRebateDetailVisible(false)}
        onExited={() => setRebateDetailContext(null)}
        target={rebateDetailContext?.target ?? null}
        rebateDetailRows={rebateDetailRows}
        summaryRow={rebateDetailSummaryRow}
        // A favorite's scenario_id is an artifact of the run it was saved from, so the
        // category names the context instead of 'Scenario N'.
        scenarioLabel={rebateDetailContext?.favorite.category}
      />
    </div>
  );
};

export default FavoriteScenarios;